import { React, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';

import { useLanguage } from '../../../../Context/LanguageContext';
import { useTheme } from '../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../Styles/theme';

import MemberShipHistoryColumn from './MemberShipHistoryColumn.js';
import CourseHistoryColumnView from './CourseHistoryColumnView.js';
import InfoPagination from '../../../../Component/MemberComponent/InfoPagination.js';
import PartnerGymMemberShipHistoryInfoView from './PartnerGymMemberShipHistoryInfo.js';
import PartnerGymCourseHistoryInfoView from './PartnerGymCourseHistoryInfoView.js';

const VALID_PARTNER_GYM_TABS = ['MemberShip', 'Course'];

export default function PartnerGymHistoryView(props) {
  const {
    memberShipData,
    division,
    lessonHistoryData
  } = props;

  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const history = useHistory();
  const location = useLocation();

  const [nowPage, setNowPage] = useState(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    return VALID_PARTNER_GYM_TABS.includes(tab) ? tab : 'MemberShip';
  });

  const [memberPage, setMemberPage] = useState(0);
  const [reservePage, setReservePage] = useState(0);
  const [useMemberShips, setUseMemberShips] = useState([]);
  const [useReserve, setUseReserve] = useState([]);

  // 탭 전환 + URL querystring 동기화 //
  function onClickTab(tab) {
    setNowPage(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    history.replace({ pathname: location.pathname, search: params.toString() });
  }

  useEffect(() => {
    if (division === 'course') onClickTab('Course');
  }, [division])

  // 컬럼 페이지네이션 //
  useEffect(() => {
    setUseMemberShips(memberShipData.slice((memberPage * 10), (memberPage * 10) + 10));
  }, [memberShipData, memberPage])

  // 컬럼 페이지네이션 //
  useEffect(() => {
    setUseReserve(lessonHistoryData.slice(reservePage * 10, (reservePage * 10) + 10));
  }, [lessonHistoryData, reservePage])

  // 회원권 페이지네이션 다음 클릭시 //
  function onClickMemberNextPage() {
    if (Math.ceil(memberShipData.length / 10) > (memberPage + 1)) setMemberPage(memberPage + 1);
  }

  // 회원권 페이지네이션 이전 클릭시 //
  function onClickMemberPrevPage() {
    if (memberPage > 0) setMemberPage(memberPage - 1);
  }

  // 출석내역 페이지네이션 다음 클릭시 //
  function onClickReserveNextPage() {
    if (Math.ceil(lessonHistoryData.length / 10) > (reservePage + 1)) setReservePage(reservePage + 1);
  }

  // 출석내역 페이지네이션 이전 클릭시 //
  function onClickReservePrevPage() {
    if (reservePage > 0) setReservePage(reservePage - 1);
  }

  return (
    <TopLevelWrapper>
      <ButtonWrapper>
        <SortButtonWrapper>
          <Button isClicked={nowPage === 'MemberShip'} onClick={() => onClickTab('MemberShip')}>{t('partnerGym.history.membership')}</Button>
          <Button isClicked={nowPage === 'Course'} onClick={() => onClickTab('Course')}>{t('partnerGym.history.courseHistory')}</Button>
        </SortButtonWrapper>
      </ButtonWrapper>
      {
        nowPage === 'MemberShip' &&
        <InfoBox>
          <MemberShipHistoryColumn />
          {
            useMemberShips.map((data, index) => (
              <PartnerGymMemberShipHistoryInfoView
                key={'PartnerGymMemberShip-' + index}
                size={useMemberShips.length}
                data={data}
                index={index} />
            ))
          }
        </InfoBox>
      }
      {
        nowPage === 'Course' &&
        <InfoBox>
          <CourseHistoryColumnView />
          {
            useReserve.map((data, index) => (
              <PartnerGymCourseHistoryInfoView key={'PartnerGymCourse-' + index} data={data} />
            ))
          }
        </InfoBox>
      }
      <PaginationWrapper>
        {
          nowPage === 'MemberShip' &&
          <InfoPagination
            nowPage={memberPage}
            totalPage={Math.ceil(memberShipData.length / 10)}
            prevClickFn={onClickMemberPrevPage}
            nextClickFn={onClickMemberNextPage}/>
        }
        {
          nowPage === 'Course' &&
          <InfoPagination
            nowPage={reservePage}
            totalPage={Math.ceil(lessonHistoryData.length / 10)}
            prevClickFn={onClickReservePrevPage}
            nextClickFn={onClickReserveNextPage}/>
        }
      </PaginationWrapper>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 20px;
  margin-bottom: 96px;

  transition: all 0.3s ease-in-out;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const SortButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 12px;
`;

const Button = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  padding: 0 20px;

  box-sizing: border-box;
  height: 40px;

  border: ${props => props.isClicked ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.buttonStroke}`};
  border-radius: 11px;
  background-color: ${props => props.isClicked ? props.theme.primary : props.theme.subBackground};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.isClicked ? props.theme.subBackground : props.theme.subText};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      border: 1px solid ${props => props.theme.primary};
      background-color: ${props => props.theme.primary};
      color: ${props => props.theme.subBackground};
    }
  }
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  min-height: 332px;

  gap: 8px;

  transition: all 0.3s ease-in-out;
`;

const PaginationWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  margin-top: 44px;
`;
